/*
 * Copyright (C) 2019 - present Marek Kuzora - All Rights Reserved.
 */


import Level from 'solis/navigation/level';

import { areDeepEqual } from 'fierry/type/compare';


export const argKey = (...names) =>
{
  return names.filter(name => name != null).join('.');
};


export const getInitialHistory = () =>
{
  const state = getStateFromURL();

  if (state.route === '/')
  {
    return [ { id: 1, level: Level.Root, ...state } ];
  }

  return [ { id: 1, level: Level.Root, route: '/', args: {} }, { id: 2, level: Level.Foreground, ...state } ];
};


export const getStateFromURL = () =>
{
  const route = window.location.pathname;

  const args = deserializeArgs(Object.fromEntries(new URLSearchParams(window.location.search)));

  return { route, args };
};


export const resolvePushState = (state, { route, args, level }) =>
{
  route = resolvePushRoute(state, route);
  level = resolvePushLevel(state, level);
  args  = resolvePushArgs(state, args, level);

  return { id: resolvePushID(state), route, args, level };
};


export const resolvePushRoute = (state, route) =>
{
  return route ? route : getEntry(state).route;
};


export const resolvePushLevel = (state, level) =>
{
  return level ? level : getEntry(state).level;
};


export const resolvePushArgs = (state, args, level) =>
{
  const inherited = getInheritedArgs(state, level);

  return resolveMergedArgs(args, inherited, inherited);
};


export const resolveReplaceArgs = (state, args = {}, level) =>
{
  return resolveMergedArgs(args, getInheritedArgs(state, level), getEntry(state).args);
};


export const getInheritedArgs = (state, level) =>
{
  return getEntry(state, getPopCountAbove(state, level))?.args ?? {};
};


export const resolveMergedArgs = (args, inherited, base) =>
{
  let results = { ...base };

  for (const [key, value] of Object.entries(args ?? {}))
  {
    if (key in inherited)
    {
      continue;
    }

    if (value == null)
    {
      delete results[key];

      clearScopedArgs(results, key, args, inherited);
    }
    else if (value !== results[key])
    {
      results[key] = value;

      clearScopedArgs(results, key, args, inherited);
    }
  }

  return results;
};


const clearScopedArgs = (results, parent, args, inherited) =>
{
  for (const key of Object.keys(results))
  {
    if (key in inherited || args[key] != null)
    {
      continue;
    }

    if (key.startsWith(parent + '.'))
    {
      delete results[key];
    }
  }
};


export const resolvePushID = (state) =>
{
  const { history } = state;

  return history.length ? history[history.length - 1].id + 1 : 1;
};


export const getStateURL = (state, publicArgs = null) =>
{
  const current = getEntry(state);

  const args = getArgsURL(current.args, publicArgs);

  return args ? (current.route + '?' + args) : current.route;
};


export const getArgsURL = (args, publicArgs = null) =>
{
  return new URLSearchParams(serializeArgs(filterPublicArgs(args, publicArgs))).toString();
};


const filterPublicArgs = (args, publicArgs) =>
{
  if (publicArgs == null)
  {
    return args;
  }

  return Object.fromEntries(Object.entries(args).filter(([key]) => publicArgs.includes(key)));
};


export const getPopCountTo = (state, level) =>
{
  let popCount = 0;

  while (popCount < state.history.length && getEntry(state, popCount).level < level)
  {
    ++popCount;
  }

  return popCount;
};


export const getPopCountBefore = (state, level) =>
{
  return getPopCountTo(state, level) + 1;
};


export const getPopCountAbove = (state, level) =>
{
  let popCount = 0;

  while (popCount < state.history.length && getEntry(state, popCount).level <= level)
  {
    ++popCount;
  }

  return popCount;
};


export const getEntry = (state, popCount = 0) =>
{
  const index = state.history.length - popCount - 1;

  return (index >= 0) ? state.history[index] : null;
};


export const popEntries = (state, popCount = 1) =>
{
  const index = state.history.length - popCount;

  return index >= 0 ? state.history.slice(0, index) : [];
};


const transformArgs = (args, callback) =>
{
  return Object.fromEntries(Object.entries(args).map(([k, v]) => [k, v != null ? callback(v) : v]));
};


const serializeArgs = (args) =>
{
  return transformArgs(args, (value) => btoa(JSON.stringify(value)));
};


const deserializeArgs = (args) =>
{
  return transformArgs(args, (value) => JSON.parse(atob(value)));
};


export const isSameEntry = (lhs, rhs) =>
{
  return lhs.route === rhs.route && lhs.level === rhs.level && areDeepEqual(lhs.args, rhs.args);
};
